import React, { Fragment, forwardRef, memo, useImperativeHandle, useState } from 'react';
import { Box, Typography, Paper, Tooltip, Collapse, TablePagination, Badge } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { usePagination } from 'src/hooks';
import { AccessControl } from 'src/components';


const useStyles = makeStyles(theme => ({
  mainContainer: {
    overflowX: 'auto',
  },

  listPaperCondensed: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
    borderRadius: theme.shape.borderRadius,
  },
  listItem: {
    cursor: ({ collapsableData }) => collapsableData ? 'pointer' : 'auto',
    '&:hover': {
      '& $actionsContainer': {
        visibility: 'visible',
        backgroundColor: 'inherit',
        animationName: '$fadeIn',
        animation: 'linear',
        animationDuration: ({ alwaysShowActions }) => alwaysShowActions ? '0ms' : '300ms',
      },
    },
  },
  listItemDefault: {
    boxShadow: '0 0 1px 0#b3bac5,0 1px 1px 0#c1c7d0',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    '&:hover': {
      transition: 'box-shadow 300ms ease 0s',
      boxShadow: 'rgb(179 186 197) 0px 0px 1px 0px, rgb(193 199 208) 0px 8px 16px -6px',
    },
  },
  listItemCondensed: {
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: theme.palette.common.gray400,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  listItemDisabled: {
    backgroundColor: theme.palette.common.gray300,
  },
  listItemHovereable: {
    '&:hover': {
      backgroundColor: theme.palette.common.lightGray,
    },
  },
  defaultItemData: {
    color: theme.palette.common.gray800,
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  actionIcons: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.gray600,
    cursor: 'pointer',
    height: theme.spacing(3),
    width: theme.spacing(4),
    '&:hover': {
      color: theme.palette.common.gray800,
      backgroundColor: theme.palette.common.lightGray,
    },
  },
  actionsContainer: {
    borderLeft: ({ isTableMode }) => isTableMode ? `1px solid ${theme.palette.common.gray400}` : '',
    marginLeft: ({ actionsCount }) => theme.spacing(4) * -1 * actionsCount - theme.spacing(1.5),
    padding: ({ isTableMode }) => isTableMode ? 0 : 0,
    paddingLeft: ({ isTableMode }) => isTableMode ? theme.spacing(2) : 1.5,
    paddingRight: ({ isTableMode }) => isTableMode ? theme.spacing(2) : 0,
    // NOTA: en algún momento intentaremos cambiar "absolute" para que las acciones queden "sticky" a la derecha y se puedan
    // ver sin necesidad de hacer scroll horizontal, el problema es que eso rompe el scroll vertical (cuando lo hay), ojo con eso
    position: ({ isTableMode }) => isTableMode ? 'absolute' : 'sticky',
    right: 0,
    backgroundColor: theme.palette.common.white,
    visibility: ({ alwaysShowActions }) => alwaysShowActions ? 'block' : 'hidden',

    top: ({ isTableMode }) => isTableMode ? '50%' : 'none',
    transform: ({ isTableMode }) => isTableMode ? 'translateY(-50%)' : 'none',
  },

  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },

  tableContainer: {
    overflowX: 'auto',
    display: 'inline-flex',
    flexFlow: 'column nowrap',
    minWidth: '100%',
    height: '100%',
  },
  tableHeader: {
    display: 'flex',
    flexFlow: 'row nowrap',
    width: '100%',
    flex: '0 0 auto',
  },
  tableRow: {
    display: 'flex',
    flex: '1 1 auto',
    position: 'relative',
  },
  tableHeaderCell: {
    color: theme.palette.common.gray800,
    fontWeight: 600,
  },
  tableRowCell: {
    color: theme.palette.common.gray800,
    fontWeight: 500,
  },
  tableCell: {
    minWidth: '120px',
    flex: 1,
    height: '100%',
    overflowWrap: 'anywhere',
    paddingLeft: theme.spacing(1),
  },
  paginationFont: {
    fontSize: '0.75rem',
  },
  highlightedEndRow: {
    backgroundColor: theme.palette.common.gray400,
    fontWeight: 700,
    '&:hover': {
      backgroundColor: theme.palette.common.gray300,
    },
  },
}));

/* eslint-disable react/prop-types */
const DefaultContent = ({ itemData }) => {
  const classes = useStyles();
  return (
    <>
      <Box width="50%">
        <Typography variant="body2" className={classes.defaultItemData}>{itemData?.name}</Typography>
      </Box>
      <Box width="50%">
        <Typography variant="body2" className={classes.defaultItemData}>{itemData?.responsibleUser}</Typography>
      </Box>
    </>
  );
};

/* eslint-disable react/prop-types */
const ContentWithHeader = ({ item, columns }) => {
  const classes = useStyles();
  return (
    <>
      {columns.map((c, index) =>
        <Typography
          key={index}
          className={clsx(classes.tableRowCell, classes.tableCell)}
          {...(c.width ? { style: { minWidth: c.width } } : {})}
          variant="body2"
          component="div"
        >
          {c.render ? c.render(item) : item[c.field]}
        </Typography>,
      )}
    </>
  );
};

/* eslint-disable react/prop-types */
const ActionsComponent = ({ actions, item, actionsClass, isTableMode }) => {
  const actionsResult = actions(item);
  const classes = useStyles();
  return (
    <Box display="flex" className={actionsClass}>
      {actionsResult.map(({ tooltip = '', Icon, onClick, accessPermission = '', badgeNumber = 0 }, index) =>
        <AccessControl action={accessPermission} key={`action-${index}`}>
          <Tooltip key={`action-${index}`} title={tooltip}>
            <Box {...(isTableMode ? { onClick } : {})} display="flex" width="100%" alignItems="center"
              className={classes.actionIcons} px={0.5}>
              {badgeNumber ?
                <Badge badgeContent={badgeNumber} color="primary" overlap="rectangular">
                  <Icon {...(isTableMode ? {} : { onClick })} />
                </Badge> :
                <Icon {...(isTableMode ? {} : { onClick })} />
              }
            </Box>
          </Tooltip>
        </AccessControl>,
      )}
    </Box>
  );
};

const CardsWithMemo = forwardRef((props, _ref) => {
  const {
    values: list = [],
    variant = 'default',
    actions,
    collapsableData,
    Content,
    columns = [],
    onHover,
    onLeave,
    checkItemDisabled,
    alwaysShowActions = false,
    showPagination = false,
    highlightLastRow = false,
    classes: { defaultItem: defaultItemCustom } = {},
  } = props;

  const isTableMode = columns.length > 0;

  const classes = useStyles({ variant, collapsableData, isTableMode, alwaysShowActions });

  const pagination = usePagination({ data: list });
  const { jump, currentPage, itemsPerPage, getCurrentData, setItemsPerPage } = pagination;

  const actionsContainerClass = classes.actionsContainer;

  const isCondensed = variant === 'condensed';
  const isDefault = variant === 'default';

  const [ collapsedItems, setCollapsedItems ] = useState(list.map(f => f.id));

  const handleChangePage = (event, newPage) => jump(newPage);

  const handleChangeRowsPerPage = event => {
    setItemsPerPage(parseInt(event.target.value, 10));
    jump(0);
  };

  useImperativeHandle(_ref, () => ({
    getPagination: () => pagination,
  }));

  const handleCollapseItem = itemId => {
    setCollapsedItems(prevItems => prevItems.includes(itemId) ? prevItems.filter(iId => iId !== itemId) : [ itemId, ...prevItems ]);
  };

  const listToShow = showPagination ? getCurrentData() : list;

  return (
    <Box className={clsx({ [classes.mainContainer]: isTableMode })}>
      {isCondensed &&
      <>
        <Paper className={clsx(classes.listPaperCondensed, { [classes.tableContainer ]: isTableMode })} elevation={0}>
          {isTableMode &&
            <Box display="flex" alignItems="center" px={2} py={0.5}
              className={clsx(classes.listItem, classes.listItemCondensed, { [classes.tableHeader]: isTableMode })}
            >
              {columns.map((c, index) =>
                <Typography
                  key={index}
                  className={clsx(classes.tableHeaderCell, classes.tableCell)}
                  style={{
                    ...(c.width ? { minWidth: c.width } : {}),
                    ...(c.headerTextAlign ? { textAlign: c.headerTextAlign } : {}),
                  }}
                  variant="body2"
                >
                  {c.title}
                </Typography>,
              )}
            </Box>
          }
          {list.length === 0 && <Box px={2} py={0.5}><Typography variant="body2">No se han agregado elementos</Typography></Box>}
          {list.length > 0 &&
            listToShow
              .map((item, index) => {
                const itemDisabled = checkItemDisabled ? checkItemDisabled(item) : item.isDisabled;
                const isLastRow = highlightLastRow && index === listToShow.length - 1;
                return <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  px={2}
                  py={0.5}
                  {...(onHover && { onMouseEnter: () => onHover(item) })}
                  {...(onLeave && { onMouseLeave: () => onLeave(item) })}
                  className={
                    clsx(
                      classes.listItem,
                      classes.listItemCondensed,
                      {
                        [classes.tableRow]: isTableMode,
                        [classes.listItemDisabled]: itemDisabled,
                        [classes.listItemHovereable]: !itemDisabled,
                        [classes.highlightedEndRow]: isLastRow,
                      },
                    )
                  }
                >
                  {
                    Content ? <Box width="100%"><Content itemData={item} /></Box>
                    : isTableMode ? <ContentWithHeader item={item} columns={columns} />
                    : <DefaultContent itemData={item} />
                  }
                  {
                    actions &&
                      <ActionsComponent item={item} actions={actions} isTableMode={isTableMode} actionsClass={actionsContainerClass} />
                  }
                </Box>;
              })
          }
        </Paper>
        {showPagination &&
            <Box display="flex" justifyContent="right">
              <TablePagination
                classes={{ caption: classes.paginationFont }}
                component="div"
                count={list.length}
                page={(currentPage + 1) > Math.ceil(list.length / itemsPerPage) ? 0 : currentPage}
                onPageChange={handleChangePage}
                rowsPerPage={itemsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[ 5, 10 ].filter(() => list.length > 5)}
                labelRowsPerPage="Elementos por página"
                nextIconButtonProps={{ size: 'small' }}
                backIconButtonProps={{ size: 'small' }}
                labelDisplayedRows={({ from, to, count }) => <>{from}-{to} de {count !== -1 ? count : <>más de {to}</>}</>}
              />
            </Box>
        }
      </>}

      {isDefault &&
        <>
          {list.length === 0 && <Box px={2} py={0.5}><Typography variant="body2">No se han agregado elementos</Typography></Box>}
          {list.length > 0 &&
            list.map((item, index) =>
              <Fragment key={index}>
                <Paper
                  className={clsx(classes.listItem, classes.listItemDefault, defaultItemCustom)}
                  onClick={() => handleCollapseItem(item.id)}
                  {...(onHover ? { onMouseEnter: () => onHover(item), onMouseLeave: () => onLeave(item) } : {})}
                >
                  <Box display="flex" alignItems="center">
                    { Content ? <Content itemData={item} /> : <DefaultContent itemData={item} /> }
                    { actions &&
                      <ActionsComponent item={item} actions={actions} isTableMode={isTableMode} actionsClass={actionsContainerClass} />
                    }

                  </Box>
                </Paper>
                {collapsableData &&
                  <Box display="flex" justifyContent="end">
                    <Box width="95%">
                      <Collapse in={collapsedItems.includes(item.id)} timeout="auto" unmountOnExit>
                        <collapsableData.Component itemData={item[collapsableData.key]} />
                      </Collapse>
                    </Box>
                  </Box>
                }
              </Fragment>
              ,
            )
          }
        </>
      }
    </Box>
  );
});

const Cards = memo(CardsWithMemo);

Cards.propTypes = {
  values: PropTypes.array,
  variant: PropTypes.string,
  actions: PropTypes.func,
  collapsableData: PropTypes.shape({
    key: PropTypes.string.isRequired,
    Component: PropTypes.func.isRequired,
  }),
  Content: PropTypes.func,
  checkItemDisabled: PropTypes.func,
  alwaysShowActions: PropTypes.bool,
  highlightLastRow: PropTypes.bool,
};


export {
  Cards,
};
